import React, { useRef, useEffect } from 'react';
import { t } from '../i18n';
import { useNavigate } from 'react-router-dom';
import './ApplicationDetail.css';

// Custom hook for scroll animations
function useScrollAnimation() {
  const ref = useRef();
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );
    
    element.classList.add('animate-on-scroll');
    observer.observe(element);
    
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);
  
  return ref;
}

const ApplicationDetail = ({ applicationNumber, language = 'bg', onClose }) => {
  const heroRef = useScrollAnimation();
  const detailsRef = useScrollAnimation();
  const benefitsRef = useScrollAnimation();
  const processRef = useScrollAnimation();
  const resultsRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();
  const navigate = useNavigate();

  // Factual application data with comprehensive content
  const applicationData = {
    1: {
      fullTitle: 'Козметична терапия с екзозоми',
      scientificBasis: 'Екзозомите съдържат растежни фактори, цитокини и микро-РНК, които стимулират регенерацията на кожните клетки. Тези наночастици действат като междуклетъчни комуникатори, доставяйки биоактивни молекули директно в клетките-мишени.',
      detailedMechanism: 'Когато се прилагат топично или чрез микроинжекции, екзозомите проникват в епидермиса и дермиса, където освобождават своето съдържание от протеини, нуклеинови киселини и липиди. Те активират клетъчните сигнални пътища, отговорни за колагеновата синтеза, клетъчното делене и тъканната регенерация.',
          benefits: [
        'Увеличава производството на колаген тип I и III с до 85%',
        'Подобрява еластичността на кожата с до 30% за 6 седмици',
        'Намалява фините линии и бръчки с видими резултати след 2 седмици',
        'Подобрява хидратацията и сияйността на кожата',
        'Стимулира клетъчното обновяване и регенерация',
        'Намалява възпалението и червенината',
        'Подобрява тонуса и текстурата на кожата'
          ],
      procedure: 'Процедурата започва с грижливо почистване на кожата, последвано от прилагане на екзозоми чрез микроинжекции или мезотерапия в дермалния слой. Използват се ултра-фини игли за минимален дискомфорт. Процедурата отнема 30-45 минути.',
      aftercare: 'След процедурата се препоръчва избягване на директна слънчева светлина за 24 часа, прилагане на хидратиращи серуми и SPF защита. Не се препоръчва интензивен спорт в първите 24 часа.',
      results: 'Първите резултати се виждат след 7-14 дни под формата на подобрена хидратация и сияние. Оптималните резултати се постигат след 4-6 седмици с видимо намаляване на фините линии и подобрена еластичност.',
      sessions: 'Препоръчват се 3-4 сесии с интервал от 4 седмици за оптимални резултати. Поддържащи процедури на всеки 6 месеца.',
      contraindications: 'Бременност, кърмене, активни кожни инфекции, автоимунни заболявания, прием на кръворазреждащи медикаменти.'
    },
    2: {
      fullTitle: 'Терапия за коса с екзозоми',
      scientificBasis: 'Екзозомите активират стволовите клетки на космените фоликули и стимулират анагенната фаза на растеж. Те съдържат специфични растежни фактори като VEGF, FGF-7, IGF-1 и PDGF, които са ключови за здравето на космените фоликули.',
      detailedMechanism: 'Микроинжектираните в скалпа екзозоми проникват до корените на косата, където освобождават растежни фактори и сигнални молекули. Това стимулира кръвообращението, подхранва фоликулите и удължава фазата на активен растеж на косата.',
          benefits: [
        'Увеличава дебелината на космените нишки с до 23% за 3 месеца',
        'Стимулира растежа на нова коса в неактивни фоликули',
        'Подобрява кръвообращението в скалпа с до 40%',
        'Намалява воспалението, което причинява косопад',
        'Удължава анагенната фаза на растеж',
        'Подобрява качеството и блясъка на косата',
        'Намалява чупливостта на косата',
        'Ефективно при андрогенна алопеция и телогенов ефлувиум'
          ],
      procedure: 'Процедурата включва детайлна диагностика на скалпа с дермоскопия, последвана от микроинжекции на екзозоми директно в скалпа на дълбочина 2-3 мм. Използва се специална техника с множество малки инжекции за равномерно покритие.',
      aftercare: 'Избягвайте миене на косата за 24 часа. Не използвайте агресивни шампоани или химически продукти 48 часа след процедурата. Избягвайте сауна и интензивен спорт първия ден.',
      results: 'Спирането на косопада се наблюдава след 4-6 седмици. Новият растеж започва да е видим след 8-12 седмици. Максималните резултати се виждат след 6 месеца с значително увеличение на плътността.',
      sessions: 'Препоръчителна схема: 4-6 сесии на всеки 2 седмици, последвани от поддържащи процедури на всеки 3 месеца.',
      contraindications: 'Активни инфекции на скалпа, автоимунни заболявания, бременност, прием на имуносупресори.'
    },
    3: {
      fullTitle: 'Комплексна грижа за кожата с екзозоми',
      scientificBasis: 'Екзозомите модулират клетъчната комуникация и активират защитните механизми на кожата. Те съдържат над 1000 различни протеина, микро-РНК и липиди, които работят синергично за възстановяване на кожната бариера.',
      detailedMechanism: 'Прилагането на екзозоми стимулира фибробластите, кератиноцитите и меланоцитите да функционират оптимално. Те регулират себумната продукция, ускоряват клетъчния обмен и подобряват бариерната функция на кожата.',
          benefits: [
        'Нормализира себумната продукция за всички типове кожа',
        'Намалява акне и поствоспалителна хиперпигментация с до 70%',
        'Подобрява бариерната функция на кожата',
        'Стимулира естествената детоксикация на клетките',
        'Изравнява тена и намалява пигментните петна',
        'Стяга порите и подобрява текстурата',
        'Увеличава естествената хидратация',
        'Намалява признаците на фотостареене'
      ],
      procedure: 'Комбинирано приложение включва дълбоко почистване, ексфолиация, прилагане на екзозоми чрез серуми, маски и микроинжекции според типа кожа. Процедурата е персонализирана според индивидуалните нужди.',
      aftercare: 'Прилагайте препоръчаните домашни продукти с екзозоми. Използвайте SPF 30+ ежедневно. Избягвайте агресивни ексфолианти първите 48 часа.',
      results: 'Подобрение в текстурата се вижда след 1 седмица. Намаляване на акне и възпаление след 2 седмици. Пълен ефект с изравнен тен след 6-8 седмици.',
      sessions: 'За оптимални резултати: 6 сесии на всеки 2 седмици, последвани от месечни поддържащи процедури.',
      contraindications: 'Активни кожни инфекции, розацеа в остра фаза, екзема, прием на ретиноиди.'
    },
    4: {
      fullTitle: 'Регенеративна медицина с екзозоми',
      scientificBasis: 'Екзозомите пренасят биоактивни молекули, които регулират възпаление и тъканна регенерация. Те модулират имунната реакция, стимулират ангиогенезата и активират стволовите клетки в тъканите.',
      detailedMechanism: 'При приложение екзозомите освобождават противовъзпалителни цитокини, растежни фактори и регенеративни сигнали. Те подобряват клетъчната миграция, ускоряват заздравяването и възстановяват функцията на увредените тъкани.',
          benefits: [
        'Ускорява заздравяването на рани с до 40%',
        'Намалява възпалението и болката значително',
        'Стимулира ангиогенезата (образуване на нови кръвоносни съдове)',
        'Подобрява функцията на увредени тъкани',
        'Намалява образуването на белези',
        'Модулира имунната реакция',
        'Стимулира регенерацията на нервни влакна',
        'Ефективно при артрит, тендинит и мускулни травми'
          ],
      procedure: 'Прилага се интрадермално, интрамускулно или интравенозно според индикацията. При локални приложения се използват микроинжекции в засегнатата област. При системни приложения се използва интравенозна инфузия.',
      aftercare: 'Спазвайте покой на третираната област за 24-48 часа. Прилагайте студени компреси при отоци. Избягвайте антивъзпалителни медикаменти първите 48 часа.',
      results: 'Намаляване на възпалението след 48-72 часа. Подобрение в болката след 3-5 дни. Тъканна регенерация в рамките на 2-4 седмици с прогресивно подобрение.',
      sessions: 'Схемата варира според състоянието: 3-5 сесии за остри състояния, 6-8 сесии за хронични проблеми с интервал 1-2 седмици.',
      contraindications: 'Активни инфекции, злокачествени заболявания, тежки имунодефицити, кърмене при интравенозно приложение.'
    }
  };

  const currentApp = applicationData[applicationNumber];

  if (!currentApp) {
    return null;
  }

  return (
    <div className="application-detail-overlay" onClick={onClose}>
      <div className="application-detail-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="modal-header" ref={heroRef}>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
          <h1>{t(currentApp.fullTitle, language)}</h1>
          </div>

        {/* Content */}
        <div className="modal-content">

          {/* Scientific Basis */}
          <section className="detail-section" ref={detailsRef}>
            <h2>{t('Научна основа', language)}</h2>
            <p>{t(currentApp.scientificBasis, language)}</p>
        </section>

          {/* Detailed Mechanism */}
          <section className="detail-section" ref={processRef}>
            <h2>{t('Детайлен механизъм', language)}</h2>
            <p>{t(currentApp.detailedMechanism, language)}</p>
        </section>

        {/* Benefits */}
          <section className="detail-section" ref={benefitsRef}>
            <h2>{t('Ползи и ефекти', language)}</h2>
            <ul className="benefits-list">
              {currentApp.benefits.map((benefit, index) => (
                <li key={index} className="benefit-item">
                <span className="benefit-icon">✓</span>
                  {t(benefit, language)}
              </li>
            ))}
          </ul>
        </section>

          {/* Procedure */}
          <section className="detail-section" ref={processRef}>
            <h2>{t('Процедура', language)}</h2>
            <p>{t(currentApp.procedure, language)}</p>
          </section>

          {/* Aftercare */}
          <section className="detail-section" ref={processRef}>
            <h2>{t('Следдиализация', language)}</h2>
            <p>{t(currentApp.aftercare, language)}</p>
          </section>

          {/* Results */}
          <section className="detail-section" ref={resultsRef}>
            <h2>{t('Очаквани резултати', language)}</h2>
            <p>{t(currentApp.results, language)}</p>
          </section>

          {/* Sessions */}
          <section className="detail-section" ref={processRef}>
            <h2>{t('Сесии', language)}</h2>
            <p>{t(currentApp.sessions, language)}</p>
        </section>

          {/* Contraindications */}
          <section className="detail-section" ref={processRef}>
            <h2>{t('Противопоказания', language)}</h2>
            <p>{t(currentApp.contraindications, language)}</p>
        </section>

          {/* CTA */}
          <section className="detail-section cta-section" ref={ctaRef}>
            <h2>{t('Готови за консултация?', language)}</h2>
            <p>{t('Свържете се с нас за персонализирана консултация и план за лечение.', language)}</p>
          <div className="cta-buttons">
              <button className="cta-primary" onClick={() => navigate('/contact')}>
                {t('Запази консултация', language)}
            </button>
          </div>
        </section>

        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail; 